import { useCallback, useMemo } from 'react';

import { ANALYTICS_SOURCE, APPEARS_IN_ACTION_SUBJECT_ID } from '../../analytics.ts';
import {
	type TwgResultSurface,
	useTwgResultActioned,
	useTwgResultViewed,
} from '../../twgResultAnalytics.ts';
import type { ConnectionRowInteractionHandler } from '../../utils/connectionRowActionTypes.ts';

import type { AppearsInData } from './useConnectionSectionsData.ts';

/**
 * The panel's and modal's "Appears in" section as a Teamwork Graph surface. Its graph rows come
 * from the byline's Cypher query over AGG GraphQL; the Jira remote-link fallback rows never count.
 */
const APPEARS_IN_TWG_RESULT_SURFACE: TwgResultSurface = {
	actionSubjectId: APPEARS_IN_ACTION_SUBJECT_ID,
	source: ANALYTICS_SOURCE,
	twgFeatureName: 'confluenceConnectionsAppearsIn',
	twgSource: 'Cypher',
	queryMethod: 'GraphQL',
};

/**
 * `twgResult viewed` once per mount (so the panel and modal each report one), the first time the
 * section settles with graph rows; the returned row callback fires `twgResult actioned` for a graph
 * row. Remote-link rows are not graph results, so they count toward neither.
 */
export const useAppearsInTwgResultEvents = ({
	twgLinks,
	loading,
}: Pick<AppearsInData, 'twgLinks' | 'loading'>): ConnectionRowInteractionHandler => {
	const twgResultCount = twgLinks.length;
	const twgUrls = useMemo(() => new Set(twgLinks.map((link) => link.url)), [twgLinks]);
	useTwgResultViewed(APPEARS_IN_TWG_RESULT_SURFACE, loading ? 0 : twgResultCount);

	const fireActioned = useTwgResultActioned(APPEARS_IN_TWG_RESULT_SURFACE);
	return useCallback(
		(item, interaction) => {
			if (twgUrls.has(item.url)) {
				fireActioned(twgResultCount, {
					twgAction: interaction,
					twgEntityType: item.resourceType,
					// `product` is the contract's own attribute, so the row's product is renamed.
					entityProduct: item.product,
				});
			}
		},
		[fireActioned, twgResultCount, twgUrls],
	);
};
